import { PressEvent } from '@react-types/shared';
import { FC, FocusEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormInput, Loader } from '@calm-web/design-system';
import { OnChange } from '@calm-web/use-form';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import useDiscountedPricing, {
	MAX_COVERED_LIVES_INCREASE,
	MIN_COVERED_LIVES,
} from '@/hooks/api/useDiscountedPricing';
import { useIsMobile } from '@/hooks/layout/useIsMobile';
import { currencyStringFromCents } from '@/utils/helpers';

import { StyledButton } from '../styles';
import { CoveredLivesFields } from '../types';
import { BOOK_A_DEMO_LINK, calculatingSelectedContractedLives } from '../utils';
import messages from './messages';
import {
	LoadingWrapper,
	PricePerUserColumn,
	PricingContainer,
	PricingText,
	StyledBorder,
	StyledCtaWithButton,
	StyledFormInputContainer,
	StyledHeaderText,
	StyledHr,
	StyledText,
	Wrapper,
} from './styles';
import { ContentBlockProps, CoveredLivesProps } from './types';

const LOG_PREFIX = 'Teams';
const LOCATION = 'covered-lives';

const ContentBlock: FC<ContentBlockProps> = ({ coveredLives, navigateTo }) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [isMobile] = useIsMobile();

	const [coveredLivesCount, setCoveredLivesCount] = useState<number>(coveredLives);
	const [previousCoveredLives, setPreviousCoveredLives] = useState<number>(coveredLivesCount);
	const [shouldValidate, setShouldValidate] = useState<boolean>(false);

	const {
		totalCost,
		savingsPerUser,
		pricePerCoveredLife,
		fullPricePerUser,
		totalSavings,
		loading: isLoading,
	} = useDiscountedPricing(coveredLivesCount < MIN_COVERED_LIVES ? previousCoveredLives : coveredLivesCount);

	useEffect(() => {
		setCoveredLivesCount(coveredLives);
	}, [coveredLives]);

	useEffect(() => {
		const isOutOfBounds =
			(shouldValidate && coveredLivesCount < MIN_COVERED_LIVES) ||
			coveredLivesCount > MAX_COVERED_LIVES_INCREASE;
		if (isOutOfBounds) {
			logEvent(`${LOG_PREFIX} : Covered Lives Selector`, {
				status: 'out of bounds',
				type: coveredLivesCount < MIN_COVERED_LIVES ? 'below' : 'above',
				location: LOCATION,
				variant: '7-16-24',
				selectedCoveredLives: coveredLivesCount,
				selectedContractedLives: calculatingSelectedContractedLives(coveredLivesCount),
			});
		}
	}, [coveredLivesCount, shouldValidate, logEvent]);

	const isValid = (count: number): boolean => {
		return count <= MAX_COVERED_LIVES_INCREASE && count >= MIN_COVERED_LIVES;
	};

	const onPress = (e: PressEvent): void => {
		logEvent(`${LOG_PREFIX} : Next Button`, {
			action: 'click',
			should_validate: shouldValidate,
			is_valid: !isValid(coveredLivesCount),
			location: LOCATION,
			variant: '7-16-24',
			selectedCoveredLives: coveredLivesCount,
			selectedContractedLives: calculatingSelectedContractedLives(coveredLivesCount),
		});

		if (shouldValidate || !isValid(coveredLivesCount)) {
			return;
		}
		const coveredLivesField = { coveredLives: coveredLivesCount };
		navigateTo('info', coveredLivesField as CoveredLivesFields);
	};

	const onChange: OnChange = e => {
		const value = parseInt(e.target.value);
		if (!value) {
			setShouldValidate(false);
			setCoveredLivesCount(0);
			return;
		}
		if (value > MAX_COVERED_LIVES_INCREASE) {
			setShouldValidate(true);
		}
		if (value >= MIN_COVERED_LIVES && value <= MAX_COVERED_LIVES_INCREASE) {
			setShouldValidate(false);
			setPreviousCoveredLives(value);
		}
		setCoveredLivesCount(value);
	};

	const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
		if (parseInt(e.target.value) < MIN_COVERED_LIVES) {
			setShouldValidate(true);
		}
	};

	const analyticsContext = {
		location: LOCATION,
		variant: '7-16-24',
		selectedCoveredLives: coveredLivesCount.toString(),
		selectedContractedLives: calculatingSelectedContractedLives(coveredLivesCount),
	};

	const PricingLoader: FC = () => {
		return (
			<LoadingWrapper>
				<Loader />
			</LoadingWrapper>
		);
	};

	const PricingPerYearIndividual: FC = () => {
		return (
			<>
				{isLoading ? (
					<PricingLoader />
				) : (
					<>
						<PricingText>{currencyStringFromCents(pricePerCoveredLife)}</PricingText>
						<StyledText>{formatMessage(messages.pricePerYearIndividualPerYearUpdated)}</StyledText>
						<StyledText color="gray5">
							{!isMobile ? (
								<>
									<s>
										{formatMessage(messages.pricePerYear, {
											annualPrice: currencyStringFromCents(fullPricePerUser),
										})}
									</s>
								</>
							) : (
								<>
									{formatMessage(messages.savingPerUserUpdated, {
										savingsAmount: currencyStringFromCents(savingsPerUser),
									})}
								</>
							)}
						</StyledText>
					</>
				)}
			</>
		);
	};

	const PricingPerYearTotal: FC = () => {
		return (
			<>
				{isLoading ? (
					<PricingLoader />
				) : (
					<>
						<PricingText>{currencyStringFromCents(totalCost)}</PricingText>
						<StyledText>{formatMessage(messages.pricePerYearTotalPerYearUpdated)}</StyledText>
						<StyledText color="gray5">
							{formatMessage(messages.pricePerYearTotalSavings, {
								totalSavings: currencyStringFromCents(totalSavings),
							})}
						</StyledText>
					</>
				)}
			</>
		);
	};

	return (
		<Wrapper>
			<StyledHeaderText>{formatMessage(messages.coveredLivesPricingHeading)}</StyledHeaderText>
			<StyledFormInputContainer>
				<FormInput
					aria-label={formatMessage(messages.coveredLivesNumber)}
					onChange={onChange}
					onBlur={onBlur}
					name={formatMessage(messages.coveredLivesNumber)}
					data-testid="covered-lives-input"
					value={coveredLivesCount === 0 ? '' : coveredLivesCount.toString()}
					isValid={!shouldValidate || isValid(coveredLivesCount)}
					showValidation={shouldValidate || !isValid(coveredLivesCount)}
					error={formatMessage(messages.exceedsCoveredLivesErrorCopy)}
					isCenteredText={true}
					noMargin={true}
					isLargerInput={true}
				/>
			</StyledFormInputContainer>
			{coveredLivesCount <= MAX_COVERED_LIVES_INCREASE ? (
				<>
					<PricingContainer>
						{!isMobile ? (
							<>
								<PricePerUserColumn>
									<PricingPerYearIndividual />
								</PricePerUserColumn>
								<PricePerUserColumn>
									<StyledBorder />
									<PricingPerYearTotal />
								</PricePerUserColumn>
							</>
						) : (
							<>
								<PricePerUserColumn>
									<PricingPerYearIndividual />
									<StyledHr />
									<PricingPerYearTotal />
								</PricePerUserColumn>
							</>
						)}
					</PricingContainer>
					<StyledButton
						size="md"
						onPress={onPress}
						backgroundColor="gradientCalmBrand"
						data-testid="get-setup-start-button"
					>
						{formatMessage(messages.ctaCopy)}
					</StyledButton>
				</>
			) : (
				<>
					<StyledHr />
					<StyledCtaWithButton
						context={{
							...analyticsContext,
							context: 'Over max allowable purchase',
						}}
						logPrefix={LOG_PREFIX}
						headerText={formatMessage(messages.exceedsCoveredLivesMaxHeading)}
						bodyText={formatMessage(messages.exceedsCoveredLivesMaxSubheading)}
						buttonText={formatMessage(messages.bookADemoButton)}
						href={BOOK_A_DEMO_LINK}
						isLoudCta
					/>
				</>
			)}
		</Wrapper>
	);
};

const CoveredLives: FC<CoveredLivesProps> = ({ navigateTo, defaultValue }) => {
	return <ContentBlock coveredLives={defaultValue} navigateTo={navigateTo} />;
};

export default CoveredLives;
